// NodeSelectField.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';

// Context
import { useIcons } from 'context/IconsContext';
import { useCurrentLanguage } from 'context/LanguageContext';
import { useTheme } from 'context/ThemeContext';

// Utilities
import { findEnumOption, getEnumOptionIconKey, getEnumOptionLabel, getOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { getResolvedFields } from 'utilities/NodeType';

// CSS
import './NodeSelectField.css';

// Components
import PopupOption from 'components/popup/option/PopupOption';
import Popup from 'components/popup/Popup';
import TextInput from 'components/textinput/TextInput';

function resolveEnumField(node, field, fieldName) {
  if (isEnumCompatibleFieldType(field)) return field;

  const fields = getResolvedFields(node?.type, { includeHidden: true, excludeSecured: true });
  return fields.find((entry) => entry.name === fieldName && isEnumCompatibleFieldType(entry)) || null;
}

// Icons

/**
 * NodeSelectField
 * 
 * Displays a read-only select input with a popup selector for schema-driven fields.
 * 
 * @param {object} node - Node object (for context, type label, etc.).
 * @param {object} field - Optional field definition for direct option resolution.
 * @param {string} value - Current field value.
 * @param {string} fieldName - Name of the field (for popup context).
 * @param {Function} onChange - Callback when new value is set.
 * 
 * @returns {ReactNode} - NodeSelectField.
 */
const NodeSelectField = ({ node, field, fieldName, value, onChange, previewMode = false }) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();
  const { iconsByKey } = useIcons();
  const language = useCurrentLanguage();

  // ----- STATE ----- //

  const [selectOpen, setSelectOpen] = useState(false);
  const [popupWidth, setPopupWidth] = useState(null);

  // ----- REFS ----- //

  const anchorRef = useRef(null);

  // ----- MEMOS ----- //

  const options = useMemo(() => {
    const fieldDef = resolveEnumField(node, field, fieldName);
    return resolveEnumOptionsForField(fieldDef, language);
  }, [field, fieldName, language, node]);

  const selectedValue = useMemo(() => {
    if (value == null || value === '') return '';
    const raw = String(value);
    const matched = findEnumOption(options, raw);
    return matched?.value != null ? String(matched.value) : raw;
  }, [value, options]);

  const displayValue = useMemo(() => {
    if (!selectedValue) return '';
    return getEnumOptionLabel(options, selectedValue, language);
  }, [options, selectedValue, language]);

  const popupOptions = useMemo(() => {
    const normalized = options.map((opt) => ({
      value: String(opt.value ?? ''),
      label: getOptionLabel(opt, language),
      color: opt?.color || '',
      iconKey: String(opt?.iconKey || '').trim(),
    }));
    if (normalized.some((opt) => opt.value === '')) return normalized;
    return [{ value: '', label: '', color: '', iconKey: '' }, ...normalized];
  }, [options, language]);

  const resolveDisplayIconKey = useCallback((iconKey, fallbackIconKey = '') => {
    const normalizedIconKey = typeof iconKey === 'string' ? iconKey.trim() : '';
    if (!normalizedIconKey) return fallbackIconKey;
    if (!iconsByKey) return normalizedIconKey;
    return iconsByKey[normalizedIconKey] ? normalizedIconKey : fallbackIconKey;
  }, [iconsByKey]);

  const selectedIconKey = useMemo(() => {
    return resolveDisplayIconKey(getEnumOptionIconKey(options, selectedValue), 'check');
  }, [options, resolveDisplayIconKey, selectedValue]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!selectOpen) return;
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    if (rect?.width) setPopupWidth(rect.width);
  }, [selectOpen, displayValue, options.length]);

  // ----- HANDLERS ----- //

  /**
   * handleSelect
   *
   * Handles a selection change.
   *
   * @param {string} nextValue - nextValue.
   */
  const handleSelect = (nextValue) => {
    onChange?.(nextValue);
    setSelectOpen(false);
  };

  // ----- RENDER ----- //

  return (
    <>
      <div className="node-select-field-container">

        {/* INPUT */}
        <div ref={anchorRef} className="node-select-field-input">
          <TextInput
            value={displayValue}
            readOnly
            placeholder={`Set value...`}
            onClick={previewMode ? undefined : () => setSelectOpen((prev) => !prev)}
            startIconKey={selectedIconKey}
            startIconColor={theme.foregroundMuted}
            endIconKey={previewMode ? undefined : 'keyboard_arrow_down'}
            endIconColor={theme.foregroundMuted}
            style={{ cursor: previewMode ? 'default' : 'pointer' }}
            iconSize={22}
            hybrid
          />
        </div>

      </div>

      <Popup anchorRef={anchorRef} isOpen={!previewMode && selectOpen} setOpen={setSelectOpen}>

        {/* NODE SELECT POPUP */}
        <div
          className="node-select-popup"
          style={{
            minWidth: popupWidth ? `${popupWidth}px` : undefined,
          }}
        >

          {/* NODE SELECT POPUP EMPTY */}
          {popupOptions.length === 0 && (
            <div
              className="node-select-popup-empty"
            >
              No options available
            </div>
          )}
          {popupOptions.map((option) => {
            const isSelected = option.value === selectedValue;
            const label = option.value === '' ? '' : option.label;
            const optionIconKey = resolveDisplayIconKey(option.iconKey, isSelected ? 'check' : '');
            return (
              <PopupOption
                key={option.value || '__empty__'}
                iconKey={optionIconKey || undefined}
                label={label}
                selected={isSelected}
                onClick={() => handleSelect(option.value)}
                rightContent={option.color ? (
                  <span
                    className="node-select-popup-color-dot"
                    style={{ backgroundColor: option.color }}
                  />
                ) : null}
              />
            );
          })}
        </div>
      </Popup>
    </>
  );
};

// Dev Tools
NodeSelectField.displayName = 'NodeSelectField';

export default NodeSelectField;
